import { ReactNode, forwardRef } from 'react'
import { FaExclamationTriangle } from 'react-icons/fa'

interface MyCheckboxProps {
  label: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  error?: string
  className?: string
  required?: boolean
  isInvalid?: boolean
  name: string
  variant?:
    | 'default'
    | 'primary'
    | 'filter-top'
    | 'noBg'
    | 'login'
    | 'bgWhite'
    | 'gallery'
  isMassageOnly?: boolean
  icon?: ReactNode
}

const MyCheckbox = forwardRef<HTMLInputElement, MyCheckboxProps>(
  (
    {
      label,
      checked,
      onChange,
      error,
      className = '',
      required = false,
      isInvalid = false,
      name,
      variant = 'default',
      isMassageOnly = false,
      icon,
    },
    ref,
  ) => {
    const variantClasses = {
      default: '',
      primary: 'bg-primary text-white',
      'filter-top': 'bg-gray-100 rounded-t-md',
      noBg: 'bg-transparent',
      login: 'text-sm',
      gallery: 'text-sm',
      bgWhite: 'bg-white',
    }

    const getBackgroundColor = () => {
      if (isInvalid || error) return 'bg-redlight'
      if (required && !checked) return 'bg-redlight rounded-md'
      if (checked) return 'bg-transparent'
      return ''
    }

    return (
      <div
        className={`flex items-center rounded-md mb-0 group ${className} ${
          variantClasses[variant]
        } ${getBackgroundColor()} ${
          isMassageOnly
            ? `bg-grisclair ${
                checked ? 'text-anthracite' : 'text-anthracite'
              } hover:text-anthracite`
            : ''
        }`}
      >
        <label
          htmlFor={`checkbox-${name}`}
          className={`flex items-center cursor-pointer rounded-md w-full text-sm transition-all duration-300
            ${
              variant !== 'noBg' && variant !== 'login' && variant !== 'gallery'
                ? 'hover:bg-grisclair2 px-3 py-2.5'
                : ''
            }
            ${variant === 'noBg' ? 'justify-center text-white mt-1 mb-3' : ''}
            ${variant === 'login' ? 'p-0 text-sm' : ''}
            ${variant === 'gallery' ? 'p-2 text' : ''}
          `}
        >
          <input
            type='checkbox'
            id={`checkbox-${name}`}
            className='sr-only w-full block min-h-5'
            checked={checked}
            onChange={e => onChange(e.target.checked)}
            required={required}
            ref={ref}
          />
          <span
            className={`relative inline-block min-w-4 h-4 mr-3 rounded 
              ${checked ? 'bg-anthracite' : 'bg-white border border-anthracite'}
              ${
                variant === 'noBg' ||
                variant === 'login' ||
                variant === 'gallery'
                  ? ' bg-redpink'
                  : ''
              }
              ${isInvalid ? 'border-redpink' : ''}
            `}
          >
            {checked && (
              <svg
                className='absolute w-3 h-3 text-white fill-current left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'
                viewBox='0 0 20 20'
              >
                <path d='M0 11l2-2 5 5L18 3l2 2L7 18z' />
              </svg>
            )}
          </span>
          <span
            className={
              variant === 'noBg' ? 'text-anthracite xl:text-white  ' : ''
            }
          >
            {isMassageOnly ? (
              <div className='flex items-center'>
                <FaExclamationTriangle className='text-redpink text-3xl mr-2 flex-shrink-0 group-hover:text-redpink transition-all' />
                <div>
                  <div>{label}</div>
                </div>
              </div>
            ) : (
              <div className='flex items-center'>
                {icon && <span className='mr-2'>{icon}</span>}
                {label}
              </div>
            )}
          </span>
        </label>
        {error && <div className='text-redpink text-sm mt-1'>{error}</div>}
      </div>
    )
  },
)

export default MyCheckbox
